import PlatformService from '@boServices/platformService';
import { useCallback, useEffect, useRef, useState } from 'react';

const platformService = new PlatformService();

// `enabled: false` skips fetching, e.g. while the list is not displayed.
const usePlatforms = ({ search, types = [], clientId, page, perPage, enabled = true }) => {
    // The caller rebuilds the array on every render; depend on its content instead of its
    // identity, and rebuild it from that key inside the effect.
    const typesKey = types.join(',');
    const [platforms, setPlatforms] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);

    const fetchPlatforms = useCallback(async () => {
        // A newer request cancels the previous one, whose late result must not overwrite the current list.
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        try {
            const data = await platformService.getPlatforms({
                search,
                clientId,
                page,
                perPage,
                types: typesKey ? typesKey.split(',') : [],
                signal: controller.signal,
            });
            if (controller.signal.aborted) return;
            setPlatforms(data.items ?? []);
            setTotal(data.total ?? 0);
            setError(null);
        } catch (err) {
            if (!controller.signal.aborted) setError(err);
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [search, clientId, page, perPage, typesKey]);

    useEffect(() => {
        if (!enabled) return undefined;
        fetchPlatforms();
        return () => abortRef.current?.abort();
    }, [fetchPlatforms, enabled]);

    return { platforms, total, loading, error, refresh: fetchPlatforms };
};

export default usePlatforms;
